import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Collapse,
  Box,
  Tooltip,
  Typography,
} from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  APP_MODULES,
  PLANT_MODULE_LICENCE_MAP,
} from "../../constants/appModules";
import { PLANT_MODULES } from "../../constants/plantModules";
import useModuleAccess from "../../hooks/useModuleAccess";
import { buildEstimationHref } from "../Drawers/TreeViewDrawer/treeView/utils/estimationLinks";
import {
  wbsLabel,
  extractProjectEstimationRevisions,
} from "../Drawers/TreeViewDrawer/treeView/hooks/useEmsProjectsTreeData";
import { projectTypeDiscipline } from "../../constants/projectTypes";
import ModuleIconBadge from "../ModuleIconBadge";

// A module tile's own hash (e.g. { "mcs-tab": "Bulk Materials" }) is appended
// the same way each module's tabs already deep-link into one another (see
// MCS3/Main.jsx's `mcs-tab`, BOMTab's `bom-tab`/`pnid-tab`). `record` (when
// given) is one specific Estimation revision - link straight to it instead.
// When a hash names a specific sub-tab, `standalone=1` is added too - see
// useStandaloneView - so NavTabs/PageHeader land on just that tab instead of
// the whole module's tab bar (a flat tile with no hash still opens the
// module normally, since no one sub-tab was picked).
const buildModuleHref = (plantId, item, record) => {
  if (record) {
    return buildEstimationHref({ plantId, ...record });
  }
  const queryParams = {
    ...item.query,
    ...(item.hash ? { standalone: "1" } : {}),
  };
  const query = Object.keys(queryParams).length
    ? `?${new URLSearchParams(queryParams).toString()}`
    : "";
  const hash = item.hash
    ? `#${new URLSearchParams(item.hash).toString()}`
    : "";
  return `/plant/${plantId}/${item.path}${query}${hash}`;
};

const isModuleItemVisible = (item, { hasModule, hasAnyModule }) =>
  item.modulesAny?.length
    ? hasAnyModule(item.modulesAny)
    : hasModule(item.module ?? APP_MODULES.EPCM);


/**
 * For an Estimation item (`item.wbsType` set), the WBS of that discipline
 * under this plant and their existing revisions - each `{ key, label,
 * record }`, `record` being what buildEstimationHref needs. Structure/Piping
 * revisions are embedded in `treeData.wbsByPlant` (already loaded by the
 * time this menu is usable - see ProjectsTreeSection), so these resolve with
 * no extra fetch; PV's come from `treeData.pvInputsByWbs`, populated lazily
 * per-WBS through the tree's own `loadPvInputsForWbs` (same cache the tree
 * itself uses, triggered on expand below - never fetched twice).
 */
const getEstimationEntries = (item, plantId, treeData) => {
  const wbsList = treeData?.wbsByPlant?.[plantId];
  const wbsLoading = wbsList === "loading" || wbsList === undefined;
  const relevantWbs = Array.isArray(wbsList)
    ? // By discipline, so current codes and full names ("PIE", "Estimation -
      // Piping") match as well as the legacy code ("pip") item.wbsType names.
      wbsList.filter((wbs) => projectTypeDiscipline(wbs?.type) === item.wbsType)
    : [];

  if (item.wbsType !== "pv") {
    const entries = relevantWbs.flatMap((wbs) =>
      extractProjectEstimationRevisions(wbs).map((entry) => ({
        key: `${wbs.id}-${entry.revision}`,
        label: `${wbsLabel(wbs)} — Rev ${entry.revision}`,
        record: {
          projectId: wbs.id,
          projectName: wbsLabel(wbs),
          revision: entry.revision,
          topTabLabel: item.topTabLabel,
          // Tells Piping / Instrumentation which tabs this WBS opens.
          wbsType: wbs.type,
        },
      })),
    );
    return { entries, loading: wbsLoading, relevantWbs };
  }

  const pvInputsByWbs = treeData?.pvInputsByWbs ?? {};
  const anyPvLoading = relevantWbs.some(
    (wbs) => pvInputsByWbs[wbs.id] === "loading",
  );
  const entries = relevantWbs.flatMap((wbs) => {
    const pvEntries = pvInputsByWbs[wbs.id];
    if (!Array.isArray(pvEntries)) return [];
    return pvEntries
      .filter((entry) => entry.estimationDocumentUID)
      .map((entry) => ({
        key: `${wbs.id}-${entry.revision}`,
        label: `${wbsLabel(wbs)} — Rev ${entry.revision}`,
        record: {
          projectId: wbs.id,
          projectName: wbsLabel(wbs),
          revision: entry.revision,
          topTabLabel: item.topTabLabel,
          subTabLabel: item.subTabLabel,
        },
      }));
  });
  return { entries, loading: wbsLoading || anyPvLoading, relevantWbs };
};

/**
 * Reusable shortcut for navigating between plant modules.
 * Use in plant table (pass plantId) or inside any plant module page (plantId from route).
 * @param {string} plantId - Plant ID
 * @param {boolean} compact - If true, renders as IconButton; otherwise as Button with label
 * @param {boolean} floating - If true, positions fixed top-right for use inside module pages
 * @param {object} sx - MUI sx prop for styling
 * @param {Array}  groups - Optional grouped catalogue (see constants/plantModules.js's
 *   PLANT_MODULE_GROUPS) to show instead of the default flat PLANT_MODULES list — each
 *   group is `{ label, modules: [{ key, label, path, icon, module?, modulesAny?, hash?,
 *   wbsType? }] }`.
 * @param {object} treeData - The sidebar tree's own `useEmsProjectsTreeData()` result
 *   (see ProjectsTreeSection), reused (not re-fetched) to list Estimation items' existing
 *   revisions behind an expand arrow — only meaningful together with `groups`.
 */
export default function PlantModuleNav({
  plantId,
  compact = false,
  floating = false,
  sx = {},
  groups = null,
  treeData = null,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [expandedKey, setExpandedKey] = useState(null);
  const navigate = useNavigate();
  const { hasModule, hasAnyModule } = useModuleAccess();

  const visibleModules = groups
    ? []
    : PLANT_MODULES.filter((module) =>
        hasModule(PLANT_MODULE_LICENCE_MAP[module.key] ?? APP_MODULES.EPCM),
      );

  const visibleGroups = groups
    ? groups
        .map((group) => ({
          ...group,
          modules: group.modules.filter((item) =>
            isModuleItemVisible(item, { hasModule, hasAnyModule }),
          ),
        }))
        .filter((group) => group.modules.length > 0)
    : [];

  const hasAnyVisible = groups
    ? visibleGroups.length > 0
    : visibleModules.length > 0;

  const handleClick = (e) => {
    e?.stopPropagation?.();
    setAnchorEl(e?.currentTarget ?? e);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setExpandedKey(null);
  };

  const handleModuleClick = (item, record) => {
    if (plantId) {
      navigate(buildModuleHref(plantId, item, record));
    }
    handleClose();
  };

  const handleOpenInNewTab = (event, item, record) => {
    event.stopPropagation();
    window.open(
      buildModuleHref(plantId, item, record),
      "_blank",
      "noopener,noreferrer",
    );
    handleClose();
  };

  if (!plantId || !hasAnyVisible) return null;

  const floatingSx = floating
    ? { position: "fixed", top: 80, right: 24, zIndex: 1000 }
    : {};

  const renderEstimationItem = (item) => {
    const { entries, loading, relevantWbs } = getEstimationEntries(
      item,
      plantId,
      treeData,
    );
    const isExpanded = expandedKey === item.key;
    const singleRecord = entries.length === 1 ? entries[0].record : null;

    const toggleExpand = (event) => {
      event.stopPropagation();
      if (isExpanded) {
        setExpandedKey(null);
        return;
      }
      setExpandedKey(item.key);
      if (item.wbsType === "pv") {
        relevantWbs.forEach((wbs) => treeData?.loadPvInputsForWbs?.(wbs.id));
      }
    };

    return (
      <React.Fragment key={item.key}>
        <MenuItem onClick={() => handleModuleClick(item, singleRecord)}>
          <ListItemIcon>
            <ModuleIconBadge moduleKey={item.key} size="sm" />
          </ListItemIcon>
          <ListItemText>{item.label}</ListItemText>
          <IconButton
            edge="end"
            size="small"
            aria-label={isExpanded ? "Hide revisions" : "Show revisions"}
            title={isExpanded ? "Hide revisions" : "Show revisions"}
            onClick={toggleExpand}
            sx={{
              transform: isExpanded ? "rotate(180deg)" : "none",
              transition: "transform 0.15s ease",
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
          <Tooltip title={`Open ${item.label} in a new tab`}>
            <IconButton
              edge="end"
              size="small"
              aria-label={`Open ${item.label} in a new tab`}
              onClick={(event) => handleOpenInNewTab(event, item, singleRecord)}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </MenuItem>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
              <CircularProgress size={16} />
            </Box>
          ) : entries.length === 0 ? (
            <MenuItem disabled sx={{ pl: 6 }}>
              <Typography variant="caption" color="text.secondary">
                No data yet
              </Typography>
            </MenuItem>
          ) : (
            entries.map((entry) => (
              <MenuItem
                key={entry.key}
                sx={{ pl: 6 }}
                onClick={() => handleModuleClick(item, entry.record)}
              >
                <ListItemText
                  primaryTypographyProps={{ variant: "body2" }}
                >
                  {entry.label}
                </ListItemText>
              </MenuItem>
            ))
          )}
        </Collapse>
      </React.Fragment>
    );
  };

  const renderModuleItem = (item) => {
    if (item.wbsType && treeData) {
      return renderEstimationItem(item);
    }

    return (
      <MenuItem key={item.key} onClick={() => handleModuleClick(item)}>
        <ListItemIcon>
          <ModuleIconBadge moduleKey={item.key} size="sm" />
        </ListItemIcon>
        <ListItemText>{item.label}</ListItemText>
        <Tooltip title={`Open ${item.label} in a new tab`}>
          <IconButton
            edge="end"
            size="small"
            aria-label={`Open ${item.label} in a new tab`}
            onClick={(event) => handleOpenInNewTab(event, item)}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </MenuItem>
    );
  };

  return (
    <Box
      sx={{ ...floatingSx, ...sx }}
      component="span"
      className="no-export"
      onClick={(e) => e?.stopPropagation?.()}
    >
      <Tooltip title="Quick navigation to all modules">
        {compact ? (
          <IconButton
            color="primary"
            onClick={handleClick}
            size="small"
            aria-controls={open ? "plant-module-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            <AppsIcon />
          </IconButton>
        ) : (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AppsIcon />}
            onClick={handleClick}
            aria-controls={open ? "plant-module-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            Modules
          </Button>
        )}
      </Tooltip>
      <Menu
        id="plant-module-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "plant-module-button",
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {groups
          ? visibleGroups.flatMap((group) => [
              <ListSubheader key={`group-${group.label}`} sx={{ lineHeight: 2 }}>
                {group.label}
              </ListSubheader>,
              ...group.modules.map(renderModuleItem),
            ])
          : visibleModules.map(renderModuleItem)}
      </Menu>
    </Box>
  );
}
